import type { GroupSchemaType, Modification } from '@carboncopyco/types';
import { useBoolean } from 'ahooks';
import { useState } from 'react';
import { RuleDeletionAlert } from '../RuleDeletionAlert';
import { RulesContent, RulesManagerFilters } from '../RulesManager';
import {
  type Filters,
  type Modifiable,
  useModificationFilterFlat,
} from '../hooks';
import { AIRuleListItem } from './AIRuleListItem';
import { AIRulePreview } from './AIRulePreview';
import { useAIManagerContext } from './AiManagerContextProvider';
import type { AIManagerProps, Rule } from './types';

export type AIManagerContent = Pick<
  AIManagerProps,
  | 'modifications'
  | 'pageUrl'
  | 'deleteModification'
  | 'onItemHover'
  | 'onItemUnhover'
  | 'onItemClick'
> & {
  AICreationForm: React.ReactNode;
  showCreateForm: boolean;
};

export const AIManagerContent = ({
  modifications,
  pageUrl,
  deleteModification,
  AICreationForm,
  showCreateForm,
}: AIManagerContent) => {
  const { getAllSubGroupModifications } = useAIManagerContext();
  const [showDeletionAlert, setShowDeletionAlert] = useBoolean();
  const [ruleToDelete, setRuleToDelete] = useState<Rule | null>(null);
  const {
    searchValue,
    selectedFilter,
    onFiltersChange,
    onSearchChange,
    result: filteredModifications,
  } = useModificationFilterFlat({
    modifications: modifications as unknown as Modifiable[],
    pageUrl,
  });

  return (
    <div className="mt-5">
      <RulesContent>
        <RulesManagerFilters<Filters>
          onSearchChange={onSearchChange}
          onFiltersChange={onFiltersChange}
          dropdownItems={[
            { id: 'all', label: 'All pages' },
            { id: 'current', label: 'Current page' },
          ]}
          searchValue={searchValue}
          selectedFilter={selectedFilter}
        />
        <RulesContent.CreationForm show={!!showCreateForm}>
          {showCreateForm ? AICreationForm : undefined}
        </RulesContent.CreationForm>

        {
          <RulesContent.List<Modification<GroupSchemaType.aiPromptGroup>>
            rules={(filteredModifications as unknown as Rule[])?.map(
              (rule) => ({
                id: rule.id,
                rule: rule,
              })
            )}
            renderItem={({ ruleItem }) => (
              <AIRuleListItem
                rule={ruleItem.rule}
                onDelete={(rule) => {
                  setRuleToDelete(rule);
                  setShowDeletionAlert.setTrue();
                }}
                pageUrl={pageUrl}
              />
            )}
          />
        }
      </RulesContent>
      <RuleDeletionAlert
        open={showDeletionAlert}
        onClose={setShowDeletionAlert.setFalse}
        onErrorMessage="Failed to delete"
        onConfirm={async () => {
          await deleteModification(ruleToDelete);
          setShowDeletionAlert.setFalse();
        }}
        rulePreview={
          <AIRulePreview
            rule={ruleToDelete}
            getAllSubGroupModifications={getAllSubGroupModifications}
          />
        }
      />
    </div>
  );
};
